import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { currentConfig } from "../config/environment";
import { getToken } from "../utils/auth";
import { tokenRefreshManager } from "./TokenRefreshManager";
import type { ApiResponse, ApiError } from "../types/auth.type";

export class HttpCoreService {
  private readonly axiosInstance: AxiosInstance;
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: currentConfig.coreApiBaseUrl,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    this.setupInterceptors();
  }
  private setupInterceptors(): void {
    // Request interceptor to add auth token.
    // Note: x-consumer-id and x-tenant-id headers are now injected by
    // Kong API Gateway after JWT validation (via the post-function plugin).
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Let axios set Content-Type with boundary for FormData (file uploads)
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    // Response interceptor to handle errors globally
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Handle 401 Unauthorized — attempt token refresh + retry
        if (error.response?.status === 401) {
          return tokenRefreshManager.handleUnauthorized(error);
        }
        const apiError: ApiError = {
          message:
            error.response?.data?.message ??
            error.message ??
            "An unexpected error occurred",
          status: error.response?.status ?? 500,
          code: error.response?.data?.code,
        };
        return Promise.reject(apiError);
      },
    );
  }
  private transformResponse<T>(response: AxiosResponse): ApiResponse<T> {
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {
        data: null as T,
        message: "Operation successful",
        statusCode: 204,
      };
    }
    // Handle standard responses
    const responseData = response.data;
    // If the response already has our API format
    if (
      responseData &&
      typeof responseData === "object" &&
      "statusCode" in responseData
    ) {
      return responseData as ApiResponse<T>;
    }
    // Transform plain data responses to our API format
    return {
      data: responseData as T,
      message: "Success",
      statusCode: response.status,
    };
  }
  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<T>(url, config);
    return this.transformResponse<T>(response);
  }
  async post<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response = await this.axiosInstance.post<TResponse>(
      url,
      data,
      config,
    );
    return this.transformResponse<TResponse>(response);
  }
  /** POST multipart/form-data (e.g. file upload). Request interceptor removes Content-Type so axios sets multipart/form-data with boundary. */
  async postForm<TResponse>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response = await this.axiosInstance.post<TResponse>(
      url,
      formData,
      config,
    );
    return this.transformResponse<TResponse>(response);
  }
  async put<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response = await this.axiosInstance.put<TResponse>(url, data, config);
    return this.transformResponse<TResponse>(response);
  }
  async patch<TResponse, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<TResponse>> {
    const response = await this.axiosInstance.patch<TResponse>(
      url,
      data,
      config,
    );
    return this.transformResponse<TResponse>(response);
  }
  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return this.transformResponse<T>(response);
  }
  // Utility method to get the current base URL
  getBaseUrl(): string {
    return currentConfig.coreApiBaseUrl;
  }
  /**
   * Fetch a binary resource as a Blob.
   * Uses responseType 'blob' so Axios does not JSON-parse the stream.
   */
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.axiosInstance.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data as Blob;
  }
  // Method to create a new instance with different config if needed
  createInstance(baseURL?: string): HttpCoreService {
    const instance = new HttpCoreService();
    if (baseURL) {
      instance.axiosInstance.defaults.baseURL = baseURL;
    }
    return instance;
  }
}
export const httpCoreService = new HttpCoreService();
